// Frontend entrypoint: wires the dropzone to the worker API and renders the
// validator results using the Waypoint Studios design.

import {
  createSharedReport,
  fetchSharedReport,
  validateZip,
  type ValidateResponse,
} from "./api.js";
import { initTheme, initVersionFooter } from "./chrome.js";
import { openShareModal } from "./share.js";
import { downloadBlob, fixZip } from "./fixer.js";
import { showFixOverlay } from "./fix-progress.js";
import {
  formatBytes,
  renderReport,
  renderScanning,
  renderUpload,
  type ScanningState,
} from "./render.js";
import { downloadReportPdf } from "./report-pdf.js";

const stage = document.getElementById("stage") as HTMLElement;

const MAX_BYTES = 500 * 1024 * 1024;

initTheme();
initVersionFooter();
initRouter();

// -------------------- Router --------------------

function initRouter(): void {
  const m = window.location.pathname.match(/^\/report\/([0-9a-f-]{8,})\/?$/i);
  if (m) {
    void showSharedReport(m[1]!);
  } else {
    showUpload();
  }
}

async function showSharedReport(id: string): Promise<void> {
  stage.innerHTML = "";
  const loading = document.createElement("div");
  loading.className = "status-banner info";
  loading.textContent = `Loading shared report…`;
  stage.append(loading);

  try {
    const shared = await fetchSharedReport(id);
    const response: ValidateResponse = {
      report: shared.report,
      ruleSet: shared.ruleSet,
      file: shared.file,
    };
    renderReport(
      stage,
      {
        response,
        sha256: shared.sha256,
        durationMs: shared.durationMs,
        shared: { createdAt: shared.createdAt, expiresAt: shared.expiresAt },
      },
      {
        onDownloadPdf: () =>
          downloadReportPdf({
            response,
            sha256: shared.sha256,
            durationMs: shared.durationMs,
            shared: { createdAt: shared.createdAt, expiresAt: shared.expiresAt },
            url: window.location.href,
          }),
      },
    );
  } catch (err) {
    stage.innerHTML = "";
    const banner = document.createElement("div");
    banner.className = "status-banner";
    banner.textContent = (err as Error).message;
    stage.append(banner);
  }
}

// -------------------- Flow --------------------

function showUpload(): void {
  renderUpload(stage, { onFile: (file) => void handleFile(file) });
}

async function handleFile(file: File): Promise<void> {
  if (!/\.zip$/i.test(file.name)) {
    return showError(`"${file.name}" isn't a .zip file.`);
  }
  if (file.size === 0) {
    return showError("That file is empty.");
  }
  if (file.size > MAX_BYTES) {
    return showError(
      `File is ${formatBytes(file.size)}; limit is ${formatBytes(MAX_BYTES)}.`,
    );
  }

  const startedAt = performance.now();
  let state: ScanningState = {
    fileName: file.name,
    progress: 2,
    label: "Hashing",
    lines: [`Computing SHA-256 for ${file.name} (${formatBytes(file.size)})`],
  };
  renderScanning(stage, state);

  // Compute SHA-256 client-side while we prepare the upload. We kick this off
  // first because it gets visible in the report; the upload begins as soon as
  // the digest call awaits.
  const shaPromise = sha256Hex(file);

  const progressTick = (label: string, progress: number, line: string): void => {
    state = {
      fileName: file.name,
      progress,
      label,
      lines: [...state.lines, line],
    };
    renderScanning(stage, state);
  };

  try {
    progressTick("Preparing upload", 6, "Requesting presigned upload URL");

    const data = await validateZip(file, {
      onStatus: (label) => {
        if (label.toLowerCase().startsWith("uploading")) {
          // onUploadProgress handles the % updates; skip here.
          return;
        }
        if (label === "Validating") {
          progressTick("Running checks", 92, "Worker is validating the archive");
        } else {
          progressTick(label.toUpperCase(), Math.min(state.progress + 4, 90), label);
        }
      },
      onUploadProgress: (pct) => {
        // Map the 0–100% upload into 10–85% of the overall progress bar.
        // The label is just the stage name; the renderer surfaces the
        // percentage separately so we don't duplicate it here.
        const mapped = 10 + Math.round(pct * 0.75);
        state = {
          fileName: file.name,
          progress: mapped,
          // Keep the stage label text-only; the UI already renders a dedicated
          // numeric progress percentage next to it.
          label: "Uploading",
          lines: state.lines,
        };
        renderScanning(stage, state);
      },
    });

    const sha = await shaPromise;
    const durationMs = performance.now() - startedAt;

    renderReport(
      stage,
      { response: data, sha256: sha, durationMs },
      {
        onFix: () => void runFix(file),
        onReset: () => {
          history.pushState({}, "", "/");
          showUpload();
        },
        onShare: () => void runShare({ response: data, sha256: sha, durationMs }),
        onDownloadPdf: () =>
          downloadReportPdf({
            response: data,
            sha256: sha,
            durationMs,
          }),
      },
    );

    stage.scrollIntoView({ behavior: "smooth", block: "start" });
  } catch (err) {
    showError((err as Error).message);
  }
}

async function runShare(input: {
  response: ValidateResponse;
  sha256: string;
  durationMs: number;
}): Promise<void> {
  showBanner("Creating share link…", "info");
  try {
    const { id, expiresAt } = await createSharedReport({
      report: input.response.report,
      ruleSet: input.response.ruleSet,
      file: input.response.file,
      sha256: input.sha256,
      durationMs: input.durationMs,
    });
    const url = `${window.location.origin}/report/${id}`;
    openShareModal(url, expiresAt);
    // Clear the status banner; the modal is now the focus.
    const banner = stage.querySelector(".status-banner");
    banner?.remove();
  } catch (err) {
    showError(`Couldn't create share link: ${(err as Error).message}`);
  }
}

async function runFix(file: File): Promise<void> {
  // Open the modal *before* awaiting so the overlay is visible while
  // fixZip's first pass (reading entries, building rename maps) runs.
  const overlay = showFixOverlay();
  try {
    const result = await fixZip(file);
    downloadBlob(result.blob, result.filename);
    const notice =
      result.appliedFixes.length === 0
        ? "No automatic fixes were applicable. The package is already structurally clean."
        : `Downloaded ${result.filename}. Applied: ${result.appliedFixes.join("; ")}.`;
    showBanner(notice, "info");
  } catch (err) {
    showError(`Couldn't rebuild the package: ${(err as Error).message}`);
  } finally {
    overlay.close();
  }
}

// -------------------- SHA-256 --------------------

async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// -------------------- Status banners --------------------

function showError(msg: string): void {
  renderUpload(stage, { onFile: (file) => void handleFile(file) });
  const banner = document.createElement("div");
  banner.className = "status-banner";
  banner.textContent = msg;
  stage.append(banner);
}

function showBanner(msg: string, tone: "info" | "error" = "info"): void {
  // Append to the current stage without replacing results.
  const existing = stage.querySelector(".status-banner");
  if (existing) existing.remove();
  const banner = document.createElement("div");
  banner.className = `status-banner ${tone === "info" ? "info" : ""}`.trim();
  banner.textContent = msg;
  stage.append(banner);
}
